// CallOverlay — the live call as a COMPACT SHEET over the normal chat
// (Bogdan 2026-08-12: "the chat should just be like the normal chat — button
// press to call, shows a smaller window with the call"; matches the locked
// one-chat fleet standard — the call is a MODE of the chat, not a second
// surface). Renders: status dot · timer · live transcript (small) · End.
// Errors land as a banner INSIDE the sheet; the sheet stays up so the user
// sees what happened. ⚠️ No currency symbols anywhere (App Store 1.1.6).
import { useLiveSession, type TranscriptLine, type InterviewCallContext } from "./useLiveSession";
import { useEffect, useRef, useState } from "react";
import { languageOptions, getLanguage, setLanguage } from "./language";

const S: Record<string, React.CSSProperties> = {
  scrim: { position: "fixed", inset: 0, background: "rgba(20,16,32,0.35)", zIndex: 60,
           display: "flex", alignItems: "flex-end", justifyContent: "center" },
  sheet: { width: "min(560px, 100%)", maxHeight: "62vh", display: "flex", flexDirection: "column",
           background: "var(--panel, #fbf9f4)", borderRadius: "18px 18px 0 0",
           boxShadow: "0 -8px 40px rgba(0,0,0,0.25)", padding: "14px 16px calc(14px + env(safe-area-inset-bottom))",
           gap: 10 },
  head: { display: "flex", alignItems: "center", gap: 10 },
  dot: { width: 10, height: 10, borderRadius: 999, flexShrink: 0 },
  title: { fontSize: 15, fontWeight: 700, flex: 1 },
  timer: { fontVariantNumeric: "tabular-nums", color: "var(--ink-2, #6b6580)", fontSize: 13 },
  err: { background: "#fdecec", color: "#8c2f39", border: "1px solid #f2b8bd",
         borderRadius: 10, padding: "8px 10px", fontSize: 13 },
  scroll: { flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6,
            minHeight: 90 },
  you: { alignSelf: "flex-end", maxWidth: "85%", background: "var(--accent, #6c5ce7)", color: "#fff",
         borderRadius: "12px 12px 4px 12px", padding: "6px 10px", fontSize: 14, lineHeight: 1.35 },
  coach: { alignSelf: "flex-start", maxWidth: "85%", background: "var(--card, #f0ecdf)",
           color: "var(--ink, #2a2438)", borderRadius: "12px 12px 12px 4px", padding: "6px 10px",
           fontSize: 14, lineHeight: 1.35 },
  hint: { color: "var(--ink-2, #6b6580)", fontSize: 13, textAlign: "center", padding: "18px 8px" },
  row: { display: "flex", gap: 8 },
  end: { flex: 1, border: "none", borderRadius: 12, padding: "12px 0", fontSize: 15,
         fontWeight: 700, cursor: "pointer", background: "#e74c3c", color: "#fff" },
  close: { flex: 0.5, borderRadius: 12, padding: "12px 0", fontSize: 15, fontWeight: 600,
           cursor: "pointer", background: "transparent",
           border: "1px solid var(--border, #d9d4ea)", color: "var(--ink, #2a2438)" },
};

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function CallOverlay({ onClose, onTranscript, interview }: {
  onClose: () => void;
  // Called once when the sheet closes, with the FULL conversation (across
  // "Call again" redials) — the caller persists it into the chat history.
  // Device-only by design: the transcript lives in the same local store as the
  // chat; nothing about the words ever reaches the Oriora server.
  onTranscript?: (lines: TranscriptLine[], seconds: number) => void;
  // A LIVE MOCK INTERVIEW: the session's context rides to the server, which locks
  // the interviewer persona into the ticket. Absent = the plain assistant call.
  interview?: InterviewCallContext;
}) {
  // The call's language — theirs to change before dialing.
  const [lang, setLang] = useState(getLanguage);
  const { status, error, lines, seconds, start, stop, diagLine } = useLiveSession(lang, interview);
  const live = status === "live";

  // Accumulates finished calls' transcripts so "Call again" doesn't lose the
  // earlier conversation (start() clears `lines` for the new call).
  const archiveRef = useRef<TranscriptLine[]>([]);
  const archivedRef = useRef(false);
  const totalSecondsRef = useRef(0);
  useEffect(() => {
    if ((status === "ended" || status === "error") && !archivedRef.current) {
      archivedRef.current = true;
      archiveRef.current.push(...lines.filter((l) => l.text.trim()));
      totalSecondsRef.current += seconds;
    }
    if (status === "connecting" || status === "live") archivedRef.current = false;
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => {
    stop();
    const all = [...archiveRef.current];
    // Closing mid-call: the current lines were never archived by the effect.
    if (!archivedRef.current) {
      all.push(...lines.filter((l) => l.text.trim()));
      totalSecondsRef.current += seconds;
    }
    if (all.length > 0) onTranscript?.(all, totalSecondsRef.current);
    onClose();
  };

  // The sheet IS the call: opening it dials, closing it hangs up.
  useEffect(() => { void start(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dotColor = live ? "#2ecc71" : status === "connecting" ? "#f5a623" : "#b9b3c9";
  const label = live ? (interview ? "Live mock interview" : "On call with your coach")
    : status === "connecting" ? (interview ? "Your interviewer is joining…" : "Calling…")
    : status === "error" ? "Call failed"
    : interview ? "Interview ended" : "Call ended";

  return (
    <div style={S.scrim} onClick={close}>
      <div style={S.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={S.head}>
          <span style={{ ...S.dot, background: dotColor }} />
          <span style={S.title}>{label}</span>
          {/* ANY language the model can speak — free text, never a fixed list.
              Suggestions are a shortcut and put the learner's own first. */}
          {!live && status !== "connecting" && (
            <>
              <input
                list="oriora-langs"
                value={lang}
                placeholder="Any language…"
                onChange={(e) => setLang(e.target.value)}
                onBlur={(e) => setLanguage(e.target.value)}
                style={{ fontSize: 13, padding: "5px 8px", borderRadius: 8, width: 132,
                         border: "1px solid var(--border, #d9d4ea)", background: "transparent",
                         color: "var(--ink, #2a2438)" }}
              />
              <datalist id="oriora-langs">
                {languageOptions().map((l) => <option key={l} value={l} />)}
              </datalist>
            </>
          )}
          {(live || status === "ended") && <span style={S.timer}>{fmt(seconds)}</span>}
        </div>
        {error && <div style={S.err}>{error}</div>}
        <div style={S.scroll}>
          {lines.length === 0 && (
            <div style={S.hint}>
              {status === "connecting"
                ? (interview ? "Connecting to your interviewer…" : "Connecting to your coach…")
                : interview
                  ? "A practice interview — answer out loud, ask for the next question any time. It's a mock, never help with a real interview."
                  : "Speak naturally — it listens while you talk, and you can interrupt it any time."}
            </div>
          )}
          {lines.map((l, i) => (
            <div key={i} style={l.role === "you" ? S.you : S.coach}>{l.text}</div>
          ))}
        </div>
        {/* Diagnostics stay INVISIBLE to customers — the counters still post to
            the server (operator-readable), but nothing debug-looking renders in
            the app. Set VITE_ORIORA_DIAG=1 in a dev build to see the line. */}
        {import.meta.env.VITE_ORIORA_DIAG === "1" && diagLine && (
          <div style={{ fontSize: 10, fontFamily: "ui-monospace, monospace",
                        color: "var(--ink-2, #6b6580)", opacity: 0.7, wordBreak: "break-all" }}>
            {diagLine}
          </div>
        )}
        <div style={S.row}>
          {(live || status === "connecting") ? (
            <button style={S.end} onClick={() => { stop(); }}>End call</button>
          ) : (
            <button style={S.end} onClick={() => { setLanguage(lang); void start(); }}>Call again</button>
          )}
          <button style={S.close} onClick={close}>Close</button>
        </div>
      </div>
    </div>
  );
}
